import { Close, SaveOutlined } from '@mui/icons-material';
import {
  Alert,
  AppBar,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  Divider,
  FormControlLabel,
  IconButton,
  MenuItem,
  Slide,
  TextField,
  Toolbar,
  Typography,
} from '@mui/material';
import { TransitionProps } from '@mui/material/transitions';
import React, { useCallback, useEffect, useState } from 'react';
import { fetchData } from '../../helpers/apiUtils';
import { HubBroker, HubSite } from './types';

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & { children: React.ReactElement },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

interface ComplianceRow {
  id: number;
  broker_id: number;
  site_id: number;
  risk_notice: string | null;
  risk_notice_waived: number;
  waiver_reason: string | null;
  updated_by: string | null;
  updated_at?: string;
}

interface Defaults {
  cta_marker: string;
  affiliate_disclosure: string;
}

interface OverviewEntry {
  broker_id: number;
  slug: string;
  name: string;
  state: 'ok' | 'waived' | 'missing';
  site_overrides: number;
}

interface Props {
  open: boolean;
  brokers: HubBroker[];
  sites: HubSite[];
  onClose: () => void;
}

/**
 * Compliance data for the blocks editor (Increment 2).
 *
 * This is the one place the risk warning is worded. External authors can PLACE the
 * Risikohinweis block anywhere on a page, but its text is resolved from here at render time —
 * so a wrong percentage is fixed once, not on every page, and a vendor cannot reword it.
 *
 * "Kein Risikohinweis nötig" is an explicit state, not an empty field: Freedom24 is not a CFD
 * broker and deliberately carries none. Without the distinction the live-deploy gate could not
 * tell "not maintained yet" from "deliberately none", which is why a waiver needs a reason.
 */
const ComplianceDialog: React.FC<Props> = ({ open, brokers, sites, onClose }) => {
  const [brokerId, setBrokerId] = useState<number | ''>('');
  // '' = master row (applies to every portal); otherwise a site_id override.
  const [siteId, setSiteId] = useState<number>(0);
  const [rows, setRows] = useState<ComplianceRow[]>([]);
  const [overview, setOverview] = useState<OverviewEntry[]>([]);
  const [defaults, setDefaults] = useState<Defaults>({ cta_marker: '*', affiliate_disclosure: '' });
  const [riskNotice, setRiskNotice] = useState('');
  const [waived, setWaived] = useState(false);
  const [waiverReason, setWaiverReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{
    severity: 'success' | 'warning' | 'error';
    text: string;
  } | null>(null);

  const loadDefaults = useCallback(async () => {
    const res = await fetchData<Defaults>('/api/broker-hub/compliance/defaults');
    if (res.success && res.data) setDefaults(res.data);
  }, []);

  const loadOverview = useCallback(async () => {
    const res = await fetchData<OverviewEntry[]>('/api/broker-hub/compliance/overview');
    if (res.success && res.data) setOverview(res.data);
  }, []);

  /**
   * Reload one broker's rows. `isCurrent` lets the caller abandon a response that arrived after
   * the user moved on — without it, two quick broker switches can resolve out of order and leave
   * the form showing the FIRST broker's legal text under the SECOND broker's id.
   */
  const loadRows = useCallback(
    async (id: number, isCurrent: () => boolean = () => true) => {
      setLoading(true);
      const res = await fetchData<ComplianceRow[]>(`/api/broker-hub/compliance/brokers/${id}`);
      if (!isCurrent()) return;
      setRows(res.success && res.data ? res.data : []);
      setLoading(false);
      // Keep the worklist honest after a save/delete.
      loadOverview();
    },
    [loadOverview],
  );

  useEffect(() => {
    if (!open) return;
    loadDefaults();
    loadOverview();
    setMessage(null);
  }, [open, loadDefaults, loadOverview]);

  useEffect(() => {
    if (brokerId === '') return;
    // Drop the previous broker's rows IMMEDIATELY. The form mirror below only reacts to `rows`,
    // so without this the fields keep showing the old broker's risk notice while `brokerId`
    // already points at the new one — and a save in that window writes it to the wrong broker.
    setRows([]);
    let cancelled = false;
    loadRows(Number(brokerId), () => !cancelled);
    return () => {
      cancelled = true;
    };
  }, [brokerId, loadRows]);

  // Mirror the selected (broker, scope) row into the form. Functional-free on purpose: the form
  // IS the row, so a scope switch must replace it wholesale rather than merge.
  //
  // A portal scope with no override of its own falls back to the MASTER row — that is what the
  // portal actually renders, and it is what the hint below claims is shown. Filling blanks here
  // made the hint a lie and turned an innocent save into a blank override that shadowed the
  // master (the server now deletes such a row instead of storing it, but the form should not
  // invite the mistake in the first place).
  useEffect(() => {
    const row =
      rows.find((r) => Number(r.site_id) === siteId) ??
      (siteId !== 0 ? rows.find((r) => Number(r.site_id) === 0) : undefined);
    setRiskNotice(row?.risk_notice ?? '');
    setWaived(Number(row?.risk_notice_waived ?? 0) === 1);
    setWaiverReason(row?.waiver_reason ?? '');
  }, [rows, siteId]);

  const save = async () => {
    if (brokerId === '') return;
    setBusy(true);
    setMessage(null);
    const res = await fetchData<ComplianceRow[]>(`/api/broker-hub/compliance/brokers/${brokerId}`, {
      method: 'PUT',
      body: JSON.stringify({
        site_id: siteId,
        risk_notice: riskNotice,
        risk_notice_waived: waived,
        waiver_reason: waiverReason,
      }),
    });
    if (res.success && res.data) {
      setRows(res.data);
      loadOverview();
      setMessage({ severity: 'success', text: 'Gespeichert.' });
    } else {
      setMessage({ severity: 'error', text: res.error || 'Speichern fehlgeschlagen' });
    }
    setBusy(false);
  };

  const removeOverride = async () => {
    if (brokerId === '' || siteId === 0) return;
    setBusy(true);
    const res = await fetchData<ComplianceRow[]>(
      `/api/broker-hub/compliance/brokers/${brokerId}/${siteId}`,
      { method: 'DELETE' },
    );
    if (res.success && res.data) {
      setRows(res.data);
      loadOverview();
      setMessage({
        severity: 'success',
        text: 'Portal-Abweichung entfernt — es gilt wieder der Master.',
      });
    } else {
      setMessage({ severity: 'error', text: res.error || 'Entfernen fehlgeschlagen' });
    }
    setBusy(false);
  };

  const saveDefaults = async () => {
    setBusy(true);
    const res = await fetchData<Defaults>('/api/broker-hub/compliance/defaults', {
      method: 'PUT',
      body: JSON.stringify(defaults),
    });
    if (res.success && res.data) {
      setDefaults(res.data);
      setMessage({ severity: 'success', text: 'Standardwerte gespeichert.' });
    } else {
      setMessage({ severity: 'error', text: res.error || 'Speichern fehlgeschlagen' });
    }
    setBusy(false);
  };

  const masterRow = rows.find((r) => Number(r.site_id) === 0);
  const hasOverride = siteId !== 0 && rows.some((r) => Number(r.site_id) === siteId);
  const missing = overview.filter((o) => o.state === 'missing');

  return (
    <Dialog fullScreen open={open} onClose={onClose} TransitionComponent={Transition}>
      <AppBar sx={{ position: 'relative' }}>
        <Toolbar>
          <Typography sx={{ flex: 1 }} variant="h6">
            Compliance — Risikohinweise &amp; Affiliate-Kennzeichnung
          </Typography>
          <IconButton edge="end" color="inherit" onClick={onClose} aria-label="Schließen">
            <Close />
          </IconButton>
        </Toolbar>
      </AppBar>

      <Box p={3} display="flex" flexDirection="column" gap={2} maxWidth={1000}>
        <Alert severity="info">
          Diese Texte werden beim Veröffentlichen in jede Blocks-Profilseite eingesetzt. Externe
          Autoren können den Risikohinweis <strong>platzieren</strong>, aber nicht umformulieren —
          und die Sternchen-Erklärung hängt der Renderer unabhängig davon an jede Seite an. Ohne
          gepflegte Daten lehnt der Hub einen Live-Deploy ab (Entwürfe bleiben möglich).
        </Alert>

        {message && (
          <Alert severity={message.severity} onClose={() => setMessage(null)}>
            {message.text}
          </Alert>
        )}

        {missing.length > 0 && (
          <Alert severity="warning">
            <strong>
              {missing.length} von {overview.length} Brokern ohne Compliance-Daten
            </strong>{' '}
            — deren Blocks-Profile lassen sich nicht live schalten:
            <Box display="flex" gap={0.5} flexWrap="wrap" mt={1}>
              {missing.map((m) => (
                <Chip
                  key={m.broker_id}
                  size="small"
                  label={m.name}
                  onClick={() => {
                    setBrokerId(m.broker_id);
                    setSiteId(0);
                  }}
                />
              ))}
            </Box>
          </Alert>
        )}

        <Typography variant="h6">Risikohinweis je Broker</Typography>
        <Box display="flex" gap={2} flexWrap="wrap" alignItems="center">
          <TextField
            select
            label="Broker"
            value={brokerId}
            onChange={(e) => setBrokerId(e.target.value === '' ? '' : Number(e.target.value))}
            sx={{ minWidth: 240 }}
            size="small"
          >
            <MenuItem value="">— bitte wählen —</MenuItem>
            {brokers.map((b) => (
              <MenuItem key={b.id} value={b.id}>
                {b.name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label="Geltungsbereich"
            value={siteId}
            onChange={(e) => setSiteId(Number(e.target.value))}
            sx={{ minWidth: 260 }}
            size="small"
            disabled={brokerId === ''}
          >
            <MenuItem value={0}>Alle Portale (Master)</MenuItem>
            {sites.map((s) => (
              <MenuItem key={s.id} value={s.id}>
                Nur {s.name}
                {rows.some((r) => Number(r.site_id) === s.id) ? ' — Abweichung gesetzt' : ''}
              </MenuItem>
            ))}
          </TextField>
          {loading && <CircularProgress size={20} />}
          {brokerId !== '' && !loading && (
            <Chip
              size="small"
              color={masterRow ? 'success' : 'warning'}
              label={masterRow ? 'Master gepflegt' : 'Master fehlt — Live-Deploy blockiert'}
            />
          )}
        </Box>

        {brokerId !== '' && (
          <>
            {siteId !== 0 && !hasOverride && (
              <Alert severity="info">
                Für dieses Portal gibt es noch keine Abweichung — angezeigt wird der Master. Erst
                eine Änderung hier legt beim Speichern eine portal-spezifische Zeile an; leer
                speichern heißt weiterhin „es gilt der Master“.
              </Alert>
            )}
            <TextField
              label="Risikohinweis (reiner Text)"
              value={riskNotice}
              onChange={(e) => setRiskNotice(e.target.value)}
              multiline
              minRows={2}
              disabled={waived}
              helperText="Wird beim Rendern HTML-escaped und in das Compliance-Markup gesetzt. Formatierung macht das CSS — HTML hier hat keine Wirkung."
            />
            <FormControlLabel
              control={<Checkbox checked={waived} onChange={(e) => setWaived(e.target.checked)} />}
              label="Kein Risikohinweis nötig (z. B. kein CFD-Broker)"
            />
            {waived && (
              <TextField
                label="Begründung für den Verzicht (Pflicht)"
                value={waiverReason}
                onChange={(e) => setWaiverReason(e.target.value)}
                helperText="Wird bei jedem Deploy als Warnung mitgeführt, damit niemand einen fehlenden Hinweis für einen Fehler hält."
              />
            )}
            <Box display="flex" gap={1}>
              <Button
                variant="contained"
                startIcon={busy ? <CircularProgress size={16} /> : <SaveOutlined />}
                // Also blocked while rows are loading: saving mid-fetch would persist whatever
                // the form happens to hold for a broker whose data has not arrived yet.
                disabled={busy || loading}
                onClick={save}
              >
                Speichern
              </Button>
              {hasOverride && (
                <Button color="error" variant="outlined" disabled={busy} onClick={removeOverride}>
                  Portal-Abweichung entfernen
                </Button>
              )}
            </Box>
          </>
        )}

        <Divider sx={{ my: 2 }} />

        <Typography variant="h6">Affiliate-Kennzeichnung (alle Broker, alle Portale)</Typography>
        <Alert severity="info">
          Die Sternchen-Erklärung wird bevorzugt aus dem{' '}
          <strong>Übersichtstext des jeweiligen Portals</strong> gezogen (Feld „Affiliate-Hinweis“),
          damit Profilseite und /broker-Übersicht desselben Portals nicht zweierlei behaupten. Der
          Text hier greift nur, wenn dort keiner hinterlegt ist.
        </Alert>
        <TextField
          label="Sternchen-Zeichen"
          value={defaults.cta_marker}
          onChange={(e) => setDefaults({ ...defaults, cta_marker: e.target.value })}
          // Wide enough for the helper text to sit on ONE line — the field itself only ever
          // holds a single character, but a wrapped two-line helper under a tiny box reads as
          // a layout bug.
          sx={{ maxWidth: 260 }}
          helperText="Wird an Affiliate-Buttons angehängt."
        />
        <TextField
          label="Affiliate-Hinweis (Fallback)"
          value={defaults.affiliate_disclosure}
          onChange={(e) => setDefaults({ ...defaults, affiliate_disclosure: e.target.value })}
          multiline
          minRows={2}
        />
        <Box>
          <Button
            variant="outlined"
            startIcon={<SaveOutlined />}
            disabled={busy}
            onClick={saveDefaults}
          >
            Standardwerte speichern
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
};

export default ComplianceDialog;
